import React from 'react';
import { Loader, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';

const ScanningPhase = ({
  selectedBusiness,
  isCompactMobile,
  currentScanStepLabel,
  scanProgress,
  visibleScanActivity,
  scanConfidencePct,
  mapEmbedUrl,
  mapZoomTransform,
  SCAN_STEPS,
  scanStepIndex,
  showScanDetails,
  setShowScanDetails,
  scanActivity
}) => (
  <motion.div key="scanning" className="report-stage final-report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
    <div className="report-header">
      <Loader size={40} className="check-icon spin" />
      <h3>
        {selectedBusiness?.name
          ? `Scanning ${selectedBusiness.name} & competitors`
          : (isCompactMobile ? 'Running your visibility scan' : 'Running your scan')}
      </h3>
    </div>

    <div className="report-section">
      <p className="report-section__title">Live progress</p>
      <div className="scan-progress-wrap" role="status" aria-live="polite">
        <div className="scan-progress-topline">
          <strong>{currentScanStepLabel}</strong>
          <span>{scanProgress}%</span>
        </div>
        <div className="scan-progress-track" aria-hidden="true">
          <div className="scan-progress-fill" style={{ width: `${scanProgress}%` }} />
        </div>
      </div>

      <ul className="scan-mini-steps" aria-label="Scan progress steps">
        {SCAN_STEPS.map((step, index) => {
          const done = index < scanStepIndex;
          const active = index === scanStepIndex;
          return (
            <li key={step} className={`${done ? 'done' : ''} ${active ? 'active' : ''}`.trim()}>
              {done ? <CheckCircle2 size={14} /> : <span className="mini-step-dot" aria-hidden="true" />}
              <span>{step}</span>
            </li>
          );
        })}
      </ul>
    </div>

    <div className="report-section">
      <p className="report-section__title">Scanning location</p>
      <div className="scan-map-frame" aria-label="Business location scan map">
        {mapEmbedUrl ? (
          <iframe
            title="Business location map"
            src={mapEmbedUrl}
            style={{ transform: mapZoomTransform }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div className="scan-map-placeholder">Loading map...</div>
        )}

        <div className="scan-overlay" aria-hidden="true">
          <div className="scan-grid" />
          <div className="scan-sweep" />
          <div className="scan-ping" />
          <div className="scan-reticle">
            <span className="scan-reticle-core" />
            <span className="scan-reticle-ring" />
          </div>
        </div>
      </div>
    </div>

    <div className="scan-focus-card" role="status" aria-live="polite">
      <small>Now checking</small>
      <strong>{currentScanStepLabel}</strong>
      <ul className="scan-focus-bullets" aria-label="Current scan checks">
        {visibleScanActivity.slice(0, 2).map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <div className="scan-confidence-row" aria-label="Scan confidence">
        <span>Confidence</span>
        <strong>{scanConfidencePct}%</strong>
      </div>
      <div className="scan-confidence-track" aria-hidden="true">
        <div className="scan-confidence-fill" style={{ width: `${scanConfidencePct}%` }} />
      </div>
    </div>

    <button
      className="scan-details-toggle"
      type="button"
      aria-expanded={showScanDetails}
      onClick={() => setShowScanDetails((current) => !current)}
    >
      <span>{showScanDetails ? 'Hide scan details' : 'Show scan details'}</span>
      {showScanDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </button>

    {showScanDetails && (
      <div className="scan-details-panel">
        <div className="scan-activity-panel" role="status" aria-live="polite">
          <h4>Current Checks</h4>
          <ul>
            {scanActivity.map((activity, index) => (
              <li key={activity} className={index === 0 ? 'is-active' : ''}>
                <span className="dot" />
                <p>{activity}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )}
  </motion.div>
);

export default ScanningPhase;
